<script setup>
import axios from 'axios';
import { useDateFormat } from '@vueuse/core';
import { onMounted, ref, watch, computed } from 'vue';
import { useAuthStore } from '@stores/index';
import { nameFormat, useDate, useDurationFormat } from '@utils/';
import { useRouter, useRoute } from 'vue-router';
import { FilterMatchMode } from '@primevue/core/api';
import { useI18n } from 'vue-i18n';
import { useToast } from 'primevue/usetoast';
import { useDebounceFn } from '@vueuse/core';
import DatePicker from 'primevue/datepicker';

const { t } = useI18n({ useScope: 'global' });

const route = useRoute();
const router = useRouter();
const apiUrl = import.meta.env.VITE_API_URL;
const authStore = useAuthStore();
const toast = useToast();

const loading = ref(true);
const totalUsers = ref(0);
const perPageList = [10, 20, 50, 100];

const getClosestAllowedValue = (value, allowedValues) => {
    return allowedValues.reduce((prev, curr) => (Math.abs(curr - value) < Math.abs(prev - value) ? curr : prev));
};
const limit = ref(isNaN(Number(route.query.rows)) || Number(route.query.rows) <= 0 ? 10 : getClosestAllowedValue(Number(route.query.rows), perPageList));

const currentPage = ref(Number(route.query.page) || 1);
const first = ref((currentPage.value - 1) * limit.value);
const usersList = ref([]);
const search = ref(route.query.search || '');
const sessionId = ref(route.params.sessionId);

const statuses = ref([
    { type: 'FINISHED', name: t('status.completed') },
    { type: 'BLOCKED', name: t('status.blocked') },
    { type: 'IN_PROGRESS', name: t('status.process') },
    { type: 'WAITING', name: t('status.waiting') },
    { type: 'NOT_TAKEN', name: t('status.notStarted') },
    { type: 'INVALID', name: 'Invalid' },
]);

const filters = ref({
    status: { value: [], matchMode: FilterMatchMode.IN },
    date: { value: null, matchMode: FilterMatchMode.DATE_IS },
    sortOrder: { value: -1 },
});
const statusFilter = computed(() => filters.value.status.value?.map((status) => status.type));

const dateBegin = ref(null);
const dateEnd = ref(null);

const updateFiltersFromRoute = () => {
    const statusQuery = route.query.status ? route.query.status.split(',') : [];
    filters.value.status.value = statusQuery.map((status) => statuses.value.find((s) => s.type === status));
    filters.value.date.value = route.query.date_begin && route.query.date_end ? [route.query.date_begin, route.query.date_end] : null;
    filters.value.sortOrder.value = route.query.sort === 'asc' ? 1 : -1;
};

const getSessionUsers = async () => {
    loading.value = true;
    try {
        const {
            data: {
                data: { result, total },
            },
        } = await axios.post(
            `${apiUrl}/v1/examiners/exam_details`,
            {
                session_id: sessionId.value,
                limit: limit.value,
                skip: first.value,
                date_begin: dateBegin.value,
                date_end: dateEnd.value,
                search: search.value,
                status: statusFilter.value?.length ? statusFilter.value : null,
                sort: filters.value.sortOrder.value === 1 ? 'asc' : 'desc',
            },
            {
                headers: {
                    Authorization: `Bearer ${authStore.userToken}`,
                    'Content-Type': 'application/json',
                },
            }
        );
        totalUsers.value = total;
        usersList.value = result;
    } catch (error) {
        console.error(error);
        toast.add({ severity: 'error', summary: t(`global.${error.response.data.message}`), life: 3000 });
    } finally {
        loading.value = false;
    }
};

const updateRouteQuery = (newQuery) => {
    router.push({ query: { ...route.query, ...newQuery } });
};

const perPageUpdate = async ({ rows, page }) => {
    limit.value = rows;
    currentPage.value = page + 1;
    updateRouteQuery({ page: currentPage.value, rows: limit.value });
};

const onSort = async (event) => {
    filters.value.sortOrder.value = event.sortOrder;
};

const clearFilter = () => {
    filters.value = {
        status: { value: [], matchMode: FilterMatchMode.IN },
        date: { value: null, matchMode: FilterMatchMode.DATE_IS },
        sortOrder: { value: -1 },
    };
};

const panelSearch = useDebounceFn(async (searchValue) => {
    search.value = searchValue;
    await getSessionUsers();
    updateRouteQuery({ search: search.value || undefined });
}, 1000);

watch(
    () => filters.value,
    async (newFilters) => {
        const [startDate, endDate] = newFilters.date.value || [];
        dateBegin.value = startDate ? useDateFormat(startDate, 'YYYY-MM-DD').value : null;
        dateEnd.value = endDate ? useDateFormat(endDate, 'YYYY-MM-DD').value : null;

        await getSessionUsers();
        updateRouteQuery({
            page: first.value == 0 ? 1 : currentPage.value,
            status: statusFilter.value?.length ? statusFilter.value.join(',') : undefined,
            date_begin: dateBegin.value || undefined,
            date_end: dateEnd.value || undefined,
            sort: newFilters.sortOrder.value === 1 ? 'asc' : 'desc',
            rows: limit.value,
        });
    },
    { deep: true }
);

watch(
    () => [currentPage.value, limit.value],
    async ([newPage, newLimit]) => {
        first.value = (newPage - 1) * newLimit;
        await getSessionUsers();
        updateRouteQuery({ page: newPage, rows: newLimit });
    }
);

watch(
    () => route.params.sessionId,
    async (newSessionId) => {
        sessionId.value = newSessionId;
        await getSessionUsers();
    }
);

onMounted(async () => {
    updateFiltersFromRoute();
});
</script>

<template>
    <div class="card">
        <div class="table-header">
            <Button type="button" icon="pi pi-filter-slash" :label="t('global.clear')" outlined @click="clearFilter()" />
            <IconField>
                <InputIcon class="pi pi-search search-element__icon" />
                <InputText class="search-text" @input="panelSearch(search)" v-model="search" :placeholder="t('global.search')" />
            </IconField>
        </div>
        <Spinner v-if="loading" />
        <DataTable :value="usersList" :lazy="true" :paginator="true" :rows="limit" :rowsPerPageOptions="perPageList" dataKey="id" @page="perPageUpdate" @sort="onSort" :sortOrder="filters.sortOrder.value" sortField="registered" :rowHover="true" :totalRecords="totalUsers" v-model:first="first" v-model:filters="filters" filterDisplay="menu" class="user-table" v-else>
            <template #empty> {{ t('global.noData') }}</template>
            <Column field="full_name" :header="t('global.user')" class="table_column">
                <template #body="{ data }">
                    <RouterLink :to="{ path: `/session/${data.session_id}/user/${data.id}` }" class="flex items-center gap-x-2">
                        <sAvatar :src="data.photo" :name="data.full_name" />
                        <h4 class="user-name">{{ nameFormat(data.full_name) }}</h4>
                    </RouterLink>
                </template>
            </Column>

            <Column field="registered" :header="t('global.registered')" class="table_column" sortable>
                <template #body="{ data }">
                    <RouterLink :to="{ path: `/session/${data.session_id}/user/${data.id}` }">
                        <span>{{ useDate(data.created_at) }}</span>
                    </RouterLink>
                </template>
            </Column>

            <Column field="date" :header="t('global.start')" :showFilterMatchModes="false">
                <template #body="{ data }">
                    <RouterLink :to="{ path: `/session/${data.session_id}/user/${data.id}` }">
                        <div class="table-wrapper" v-if="data.started_at">
                            <div class="table-wrapper">
                                <i class="pi pi-calendar"></i>

                                <span>{{ useDateFormat(data.started_at, 'DD.MM.YY').value }}</span>
                            </div>
                            <div class="table-wrapper">
                                <i class="pi pi-clock"></i>
                                <span>{{ useDateFormat(data.started_at, 'HH:mm:ss').value }}</span>
                            </div>
                        </div>
                        <span v-else>-</span>
                    </RouterLink>
                </template>

                <template #filter="{ filterModel }">
                    <DatePicker v-model="filterModel.value" selectionMode="range" dateFormat="yy-mm-dd" :placeholder="t('global.dateFormat')" hideOnRangeSelection />
                </template>
            </Column>

            <Column field="duration" :header="t('global.duration')">
                <template #body="{ data }">
                    <RouterLink :to="{ path: `/session/${data.session_id}/user/${data.id}` }" class="flex items-center gap-x-2">
                        <div class="table-wrapper" v-if="data.stopped_at">
                            <i class="pi pi-clock"></i>
                            <span>{{ useDurationFormat(data.stopped_at - data.started_at) }}</span>
                        </div>
                        <p v-else class="text-left">-</p>
                    </RouterLink>
                </template>
            </Column>

            <Column field="status" :header="t('global.status')" :showFilterMatchModes="false" :filterMenuStyle="{ maxWidth: '10rem' }">
                <template #body="{ data }">
                    <RouterLink :to="{ path: `/session/${data.session_id}/user/${data.id}` }">
                        <Status :status="data.status" />
                    </RouterLink>
                </template>

                <template #filter="{ filterModel }">
                    <MultiSelect v-model="filterModel.value" :options="statuses" optionLabel="name" :placeholder="t('global.status')">
                        <template #option="slotProps">
                            <Status :status="slotProps.option.type" />
                        </template>
                    </MultiSelect>
                </template>
            </Column>
        </DataTable>
    </div>
</template>
<style scoped>
.card {
    @apply bg-white p-4;
}

.user-table {
    @apply text-sm;
}

.table-wrapper {
    @apply flex items-center gap-1;
}

.table-header {
    @apply flex justify-between items-center bg-white mb-4;
}

.search-element__icon {
    @apply dark:text-[#FFFFFF10];
}

.search-text {
    @apply text-sm;
}
</style>
